import type { MeResponse, NavigationGroupDto } from '@an-crm/shared';
import { Menu, Search } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from './breadcrumbs';
import { NotificationsButton } from './notifications';
import { ThemeToggleButton } from './theme-toggle';
import { UserMenu } from './user-menu';

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export function Topbar({
  me,
  nav,
  onOpenPalette,
  onOpenMobileNav,
  onSignOut,
}: {
  me: MeResponse;
  nav: NavigationGroupDto[];
  onOpenPalette: () => void;
  onOpenMobileNav: () => void;
  onSignOut: () => void;
}) {
  const tenantName = me.tenant ? (me.tenant.branding.displayName ?? me.tenant.name) : 'Platform console';
  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-border bg-surface/80 px-4 backdrop-blur-md sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenMobileNav}
        aria-label="Open navigation"
      >
        <Menu />
      </Button>

      <div className="flex min-w-0 items-center gap-3">
        <div className="hidden items-center gap-2.5 sm:flex">
          <Avatar
            name={tenantName}
            src={me.tenant?.branding.logoUrl ?? null}
            className="size-7 rounded-md text-[11px]"
            color={me.tenant?.branding.primaryColor}
          />
          <span className="max-w-48 truncate font-display text-sm font-semibold text-fg">{tenantName}</span>
          <span className="h-5 w-px bg-border" aria-hidden />
        </div>
        <Breadcrumbs nav={nav} />
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={onOpenPalette}
          className="focus-ring hidden h-9 w-64 items-center gap-2 rounded-md border border-border bg-surface-2/60 px-3 text-sm text-fg-subtle transition-colors hover:border-border-strong hover:text-fg-muted md:flex"
          aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
        >
          <Search className="size-4" aria-hidden />
          <span className="flex-1 text-left">Search or jump to…</span>
          <kbd
            aria-hidden
            className="rounded border border-border bg-surface px-1.5 py-px font-mono text-[10.5px]"
          >
            {isMac ? '⌘K' : 'Ctrl K'}
          </kbd>
        </button>
        <Button variant="ghost" size="icon" className="md:hidden" onClick={onOpenPalette} aria-label="Search">
          <Search />
        </Button>
        <NotificationsButton readOnly={Boolean(me.support)} />
        <ThemeToggleButton />
        <div className="ml-1">
          <UserMenu me={me} onSignOut={onSignOut} />
        </div>
      </div>
    </header>
  );
}
